import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../service";

const initialState: any = {
  connected: false,
  adviserName: null,
  connectionLoading: false,
  authorizeUrl: "",
  syncLoading: false,
  syncResults: [],
  validateLoading: false,
  validationReport: null,
};

export const checkConnectionStatus = createAsyncThunk(
  "cashcalc/checkConnectionStatus",
  async () => {
    try {
      const response: any = await apiServices.cashCalc.getConnectionStatus();
      const data = response.data?.data || response.data || {};
      return { connected: data?.connected ?? false, adviserName: data?.adviserName || null };
    } catch {
      // Cashcalc MS not running or unreachable — treat as not connected
      return { connected: false, adviserName: null };
    }
  }
);

export const getAuthorizeUrl = createAsyncThunk(
  "cashcalc/getAuthorizeUrl",
  async (consultantUserId: string, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.cashCalc.getAuthorizeUrl(consultantUserId);
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || "Failed to get authorize URL");
      }
      return response.data?.data?.authorizeUrl || response.data?.authorizeUrl || null;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to get authorize URL");
    }
  }
);

export const exchangeToken = createAsyncThunk(
  "cashcalc/exchangeToken",
  async (data: { code: string; consultantUserId?: string }, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.cashCalc.exchangeToken(data);
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || "Failed to connect to CashCalc");
      }
      const d = response.data?.data || response.data || {};
      return { connected: d?.connected ?? true, adviserName: d?.adviserName || null };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const disconnectCashCalc = createAsyncThunk(
  "cashcalc/disconnect",
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.cashCalc.disconnect();
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || "Failed to disconnect");
      }
      return false;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const syncToCashCalc = createAsyncThunk(
  "cashcalc/sync",
  async (data: { customerId: string; scopes: string[] }, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.cashCalc.sync(data);
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || response.data.message || "Sync failed");
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      const detail =
        error?.response?.data?.error?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Unknown error";
      return rejectWithValue(detail);
    }
  }
);

export const validateCashCalcClient = createAsyncThunk(
  "cashcalc/validate",
  async (data: { customerId: string; scopes: string[] }, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.cashCalc.validate(data);
      if (response.data?.isError) {
        return rejectWithValue(response.data.error?.message?.[0] || response.data.message || "Validation failed");
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      const detail =
        error?.response?.data?.error?.message?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Unknown error";
      return rejectWithValue(detail);
    }
  }
);

export const cashcalcSlice = createSlice({
  name: "cashcalc",
  initialState,
  reducers: {
    clearCashCalcState: (state) => {
      state.connected = false;
      state.adviserName = null;
      state.syncResults = [];
      state.authorizeUrl = "";
    },
    // Resets sync/validate results only — keeps the CashCalc connection intact.
    // Used by the "Reset" button to prep the screen for a new sync/validate run.
    resetSyncState: (state) => {
      state.syncResults = [];
      state.validationReport = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkConnectionStatus.pending, (state) => {
        state.connectionLoading = true;
      })
      .addCase(checkConnectionStatus.fulfilled, (state, action) => {
        state.connectionLoading = false;
        state.connected = action.payload.connected;
        state.adviserName = action.payload.adviserName;
      })
      .addCase(checkConnectionStatus.rejected, (state) => {
        state.connectionLoading = false;
        state.connected = false;
      })
      .addCase(getAuthorizeUrl.fulfilled, (state, action) => {
        state.authorizeUrl = action.payload;
      })
      .addCase(exchangeToken.fulfilled, (state, action) => {
        state.connected = action.payload.connected;
        state.adviserName = action.payload.adviserName;
      })
      .addCase(disconnectCashCalc.fulfilled, (state) => {
        state.connected = false;
        state.adviserName = null;
      })
      .addCase(syncToCashCalc.pending, (state) => {
        state.syncLoading = true;
        state.syncResults = [];
      })
      .addCase(syncToCashCalc.fulfilled, (state, action) => {
        state.syncLoading = false;
        state.syncResults = action.payload?.results || [];
      })
      .addCase(syncToCashCalc.rejected, (state) => {
        state.syncLoading = false;
      })
      .addCase(validateCashCalcClient.pending, (state) => {
        state.validateLoading = true;
        state.validationReport = null;
      })
      .addCase(validateCashCalcClient.fulfilled, (state, action) => {
        state.validateLoading = false;
        state.validationReport = action.payload;
      })
      .addCase(validateCashCalcClient.rejected, (state) => {
        state.validateLoading = false;
      });
  },
});

export const { clearCashCalcState, resetSyncState } = cashcalcSlice.actions;
export default cashcalcSlice.reducer;
